import type { ReactNode } from "react";
import { Suspense, useMemo, useRef, useState } from "react";
import { Await, useSearchParams } from "react-router";

import { ProductRecommendationsServer } from "~/hec/components/recs/product-recommendations-server";

import type { BuilderContent } from "@builder.io/sdk-react";

import Breadcrumbs, { BreadcrumbsSkeleton } from "~/components/breadcrumbs";
import { VariantSelector } from "~/components/products/product-variant-selector";
import { SkeletonAwait } from "~/components/suspense/skeleton";
import { useSite } from "~/contexts";
import { CioItemDetailTracker } from "~/hec/analytics/cio-item-detail-tracker";
import { ModelMap } from "~/hec/builder/model-map";
import { PrefetchBuilderAnchors } from "~/hec/components/prefetch-builder-anchors";
import { PrefetchedBuilderContent } from "~/hec/components/prefetched-builder-content";
import { WhatsLmnButton } from "~/hec/components/product/whats-lmn-button";
import {
  accordionSectionsFor,
  hasAccordionBlock,
} from "~/hec/pdp/accordion-sections.logic";
import { resolveShortDescription } from "~/hec/pdp/short-description.logic";
import { TreatmentPrice } from "~/hec/telehealth/components/treatment-price";
import { TelehealthPdpPriceProvider } from "~/hec/telehealth/pdp-price-context";
import { treatmentByConditionType } from "~/hec/telehealth/treatments";
import type { HecProduct } from "~/hec/types/product";
import {
  isLMNProduct,
  isScheduled,
  isSetProduct,
  isTelehealthProduct,
  isVariantProduct,
} from "~/hec/types/product";
import { buildProductUrl } from "~/hec/utils/product-slug";
import { rewriteSfccContentLinks } from "~/hec/utils/sfcc-content-links";
import { bySiteId } from "~/hec/utils/site-id";
import type { C_ProductInfo } from "~/lib/app.types";
import type { SerializeFrom } from "~/lib/types";
import { cn } from "~/lib/ui";
import { invariant } from "~/lib/utils/utils";
import { useRootLayoutData } from "~/root";
import { useLayoutData } from "~/routes/layout";
import { SegmentViewItemTracker } from "~/segment/view-item-tracker";
import { getVariantImage } from "~/shop/utils/image";
import { resolvePriceFromSelectedVariant } from "~/shop/utils/price-display";
import { sanitizeHtml } from "~/utils/sanitize-html";

import type { CrossSellItem } from "../resources/schemas/basket";
import { HybridPDP } from "./Hybrid-PDP/hybrid-pdp";
import { BadgesSkeleton } from "./components/badges-skeleton";
import { BundleItems } from "./components/bundle-items";
import {
  type CompareToData,
  CompareToMessage,
} from "./components/compare-to-message";
import { FeatureContentModules } from "./components/feature-content-modules";
import { FeatureContentModulesSkeleton } from "./components/feature-content-modules-skeleton";
import { HybridPDPSkeleton } from "./components/hybrid-pdp";
import { InfoColumnSkeleton } from "./components/info-column-skeleton";
import { InlineBelowAccordionsSlot } from "./components/inline-below-accordions-slot";
import { selectInlineBelowAccordions } from "./components/inline-content-regions";
import {
  MobileTitleBrandSkeleton,
  OptimisticBadges,
  OptimisticHero,
  OptimisticInfoColumn,
  OptimisticTitleBrand,
} from "./components/optimistic-tile-seed";
import {
  PDPAccordion,
  PDPAccordionContent,
  PDPAccordionItem,
  PDPAccordionTrigger,
  PDPAccordionsSkeleton,
} from "./components/pdp-accordion";
import { PDPArticles, PDPArticlesSkeleton } from "./components/pdp-articles";
import {
  CrossSellProducts,
  HybridDetector,
  PDPTabsRender,
  PriceLabel,
} from "./components/pdp-shared";
import {
  ELIGIBILITY_LABEL_BY_SITE,
  ELIGIBILITY_SITE_LABEL_BY_SITE,
  HOW_IT_WORKS_ANCHOR_ID,
  SHOWS_FREE_SHIPPING_BADGE,
} from "./components/pdp-shared-constants";
import { PdpShortDescription } from "./components/pdp-short-description";
import {
  PowerReviewsDisplay,
  PowerReviewsHeading,
  PowerReviewsQA,
  PowerReviewsSnippet,
  PowerReviewsSyncedRatings,
  PowerReviewsWidget,
  pwrHeadlineId,
} from "./components/power-reviews";
import {
  EligibilityBadge,
  EligibilitySummaryPill,
  FreeShippingBadge,
} from "./components/product-badges";
import { ProductBuyForm } from "./components/product-buy-form";
import {
  ProductImages,
  ProductImagesSkeleton,
} from "./components/product-images";
import { ProductQuickNav } from "./components/product-quick-nav";
import {
  ProductVariantOptions,
  VariantSelectorRegion,
} from "./components/product-variant-options";
import { PromotionCallouts } from "./components/promotion-callouts";
import { StickyATCBar, StickyATCSentinel } from "./components/sticky-atc-bar";
import { PDP_CTA_CONTEXT } from "./components/telehealth/constants";
import { telehealthPricePeriod } from "./components/telehealth/price-period";
import {
  describeGlp1BuyColumn,
  hasGlp1HowItWorks,
  resolveGlp1Regions,
} from "./components/telehealth/pricing-regions";
import {
  TelehealthInfoColumn,
  TelehealthInfoColumnSkeleton,
} from "./components/telehealth/telehealth-info-column";
import { resolveVariantTitle } from "./components/variant-title";
import { useHybridDetection } from "./hooks/use-hybrid-detection";
import type { ProductPageData } from "./loader/product-detail-loader";
import {
  useProductDetailsHistoryState,
  useProductTileSeed,
} from "./location-state/product-details.location-state";
import {
  type HybridPdpProduct,
  hasHybridParam,
  isHybridPdp,
} from "./utils/hybrid-pdp";
import { renderBrandRow } from "./utils/render-brand-row";

// This file is no longer a route — the catch-all dispatcher (`app/routes/$.tsx`)
// owns the `loader`, `headers`, `ErrorBoundary` etc. and renders
// `<ProductPageContent>` when SCAPI URL Mapping resolves to PRODUCT.

export type { ProductPageData };

/**
 * Renders one server-fetched recs carousel once its loader promise resolves.
 * The resolved `PodRecs` shape (products / heading / podId / resultId /
 * strategyIds) maps 1:1 onto `ProductRecommendationsServer`'s props, so it
 * spreads directly. Shared by both PDP carousels (`itemPageRecs` above
 * Reviews, `relatedProducts` at the page foot) so the render contract — and
 * any future change to it — lives in one place. Empty results render nothing
 * (`ProductRecommendationsServer` returns null on a zero-length array).
 */
function PdpRecsCarousel({ recs }: { recs: ProductPageData["itemPageRecs"] }) {
  return (
    <Suspense fallback={null}>
      <Await resolve={recs}>
        {recs => <ProductRecommendationsServer {...recs} />}
      </Await>
    </Suspense>
  );
}

/**
 * Info-column streaming fallback for a tile-seeded PDP. Three-way because the
 * divergent columns can't reuse the optimistic one: it seeds a price + variant +
 * Add-to-Cart layout, which would paint a purchase affordance on an LMN or
 * prescription product and then swap it out one product fetch later.
 */
function renderInfoColumnFallback(
  tileSeed: SerializeFrom<HecProduct> | undefined,
  siteId: string,
) {
  if (tileSeed && usesOptimisticInfoColumn(tileSeed)) {
    return <OptimisticInfoColumn seed={tileSeed} siteId={siteId} />;
  }
  if (tileSeed && isTelehealthProduct(tileSeed)) {
    return <TelehealthInfoColumnSkeleton />;
  }
  return <InfoColumnSkeleton />;
}

/**
 * Whether a tile seed can drive the optimistic info column. LMN and telehealth
 * products both render a divergent column with no price / variant / ATC, so
 * seeding the standard one would paint a purchase affordance and then swap it
 * out one product-fetch later.
 *
 * FAILS OPEN, and is not a safety boundary: `c_ownerClass` is absent from the
 * CIO tile payload and grafted on by the PLP's SCAPI enrichment pass
 * (`product-list/loader/plp-shared-utils.server.ts`), which swallows its own
 * errors and skips products individually. When that misses, the seed looks
 * standard and the optimistic column paints for one product fetch. It's a
 * skeleton, never a live Add to Cart — the real gate is the resolved-product
 * branch in the info column. The pre-existing LMN guard has the same hole.
 */
function usesOptimisticInfoColumn(seed: {
  c_ownerClass?: string | null;
}): boolean {
  return !isLMNProduct(seed) && !isTelehealthProduct(seed);
}

/**
 * ET-216: the same carousel, suppressed on telehealth (GLP-1) PDPs — ET-213
 * hides Constructor recommendations on treatment pages ("we will test
 * incorporating this after launch").
 *
 * Gated on the RESOLVED product rather than a loader-derived flag: `data` is
 * still a promise on client `.data` navigations (`deferDataRequest`), so a
 * loader-side boolean would read `undefined` off a promise and silently
 * un-hide these on every soft nav. `fallback={null}` means there's no flash to
 * trade for it — the carousels stream in later regardless.
 */
function GatedPdpRecsCarousel({
  recs,
  product,
  className,
}: {
  className?: string;
  recs: ProductPageData["itemPageRecs"];
  // Same union the route's `product` local carries: the loader value, which is
  // still a promise on client `.data` navigations, or the history-state product.
  product: C_ProductInfo | ProductPageData["data"];
}) {
  return (
    <Suspense fallback={null}>
      <Await resolve={product}>
        {resolved =>
          isTelehealthProduct(resolved as { c_ownerClass?: string }) ? null : (
            // Wrapper lives INSIDE the gate: left outside, a treatment PDP kept
            // the carousel's vertical spacing on every render, not just on an
            // empty CIO result.
            <div className={className}>
              <PdpRecsCarousel recs={recs} />
            </div>
          )
        }
      </Await>
    </Suspense>
  );
}

/**
 * Prop-based render of the PDP. Consumed by the catch-all dispatcher
 * (`app/routes/$.tsx`) when `lookupUrlMapping` resolves to a PRODUCT hit.
 * The dispatcher's loader is shared across builder / PDP / PLP branches,
 * so this component can't call `useLoaderData<typeof loader>()` — the
 * loader return type is a discriminated union, not the PDP shape.
 *
 * `productId` is hoisted onto `loaderData` by `runProductLoader` so the
 * component reads it from props rather than `useParams()`. Under the
 * dispatcher there is no `:productId` URL segment to read.
 *
 * Site divergence (SFCC-11038 / PDP-WDH-01) is handled inline via
 * `site.id === "WDH"` conditionals — currently only `<FeatureContentModules>`
 * `includedKeys`, mirroring `app_wdh`'s `topContentModule` omission. Hybrid
 * layout (`?hybrid=true` or non-orderable) is site-neutral and composed via
 * the `HybridPDP` component.
 */
export function ProductPageContent({
  loaderData,
}: {
  loaderData: ProductPageData;
}) {
  const {
    master,
    data: _product,
    itemPageRecs,
    relatedProducts,
    crossSell,
    hybridGrids,
    staticContent,
    originalTabsContent,
    productCategories,
    powerReviewsConfig,
    inStockWarningLevel,
    pdpArticles,
    showPdpArticles,
    pdpInlineSection,
    pdpGlobalSection,
    variantDataMap,
    pdpDeliveryReturnsSection,
    pdpLoyaltySection,
    glp1PricingSection,
    acLoyaltyPoints,
    productId,
  } = loaderData;

  invariant(productId, "productId is required");

  // Will a Builder accordion (Delivery & Returns, Loyalty) render? Computed
  // once: it feeds both the accordion block's guard and the short
  // description's Read More gate, which must agree (COM-180 review).
  const hasBuilderAccordions = Boolean(
    pdpDeliveryReturnsSection || pdpLoyaltySection,
  );

  // ET-216: one `pdp-glp-1-pricing` entry drives BOTH halves of a treatment
  // PDP — the buy column's price + CTA and the below-fold How it Works / FAQ —
  // so a marketer manages them in one place. `resolveGlp1Regions` owns which
  // of the entry's regions feeds which half (and why); it degrades to the
  // legacy main-canvas-is-the-buy-column layout for entries that predate the
  // `buyColumn` field.
  // Memoized because it returns fresh object identities: without it every
  // unrelated PDP state change (cross-sell totals, sticky-ATC toggles) hands
  // the Builder subtrees a new `content` reference and re-renders them.
  const glp1Regions = useMemo(
    () => resolveGlp1Regions(glp1PricingSection),
    [glp1PricingSection],
  );
  // COM-793: the `belowAccordions` region of the same `pdp-inline-content`
  // entry, rendered below the product accordions. The entry's main canvas
  // keeps rendering above them (see the `belowFoldSections.push` further down
  // — despite its name, that list is the ABOVE-accordions slot). Memoized for
  // the same fresh-identity reason as `glp1Regions`.
  const inlineBelowAccordions = useMemo(
    () => selectInlineBelowAccordions(pdpInlineSection),
    [pdpInlineSection],
  );
  const glp1BuyColumnSection = glp1Regions.buyColumn;
  // Which halves the authored region supplies. The coded price / CTA fills in
  // whichever it doesn't, so a partially-authored entry can't strand a
  // treatment PDP with no purchase path.
  const glp1BuyColumnProvides = useMemo(
    () => describeGlp1BuyColumn(glp1BuyColumnSection),
    [glp1BuyColumnSection],
  );
  // Whether a `TelehealthHowItWorks` is actually authored below the fold, which
  // is what the buy column's jump link claims to lead to.
  const glp1HasHowItWorks = useMemo(
    () => hasGlp1HowItWorks(glp1Regions.belowFold),
    [glp1Regions],
  );
  // The below-fold slot stacks EVERY source rather than picking one, so a
  // treatment PDP can carry a `pdp-glp-1-pricing` entry and a
  // `pdp-inline-content` entry at the same time (each authored + previewed
  // through its own model) instead of the GLP-1 entry silently suppressing the
  // inline one. GLP-1 regions go first — they hold the How it Works the buy
  // column's anchor link jumps to. Core PDPs are unaffected: they only ever
  // have the inline entry, so the list is the single item it always was.
  //
  // Keys combine the region with the entry id, never a position. The region
  // alone would collide across products (two migrated entries both yield
  // `canvas`), and the PDP route is reused across client-side navigations
  // between treatment PDPs — so a region-only key would let React reuse one
  // `<Content>` instance, which seeds its state from `content` in `useState`
  // initializers, across two different medications' content.
  const belowFoldSections: {
    key: string;
    model: string;
    content: BuilderContent;
  }[] = glp1Regions.belowFold.map(({ source, content }) => ({
    key: `glp1-${source}-${content.id ?? "no-id"}`,
    model: ModelMap.pdpGlp1Pricing,
    content,
  }));
  if (pdpInlineSection) {
    belowFoldSections.push({
      key: "pdp-inline-content",
      model: ModelMap.pdpInlineContent,
      content: pdpInlineSection,
    });
  }

  const staticContentData = staticContent;

  const [state] = useProductDetailsHistoryState();
  // Seed carried from a list/rec tile click — drives optimistic above-the-fold
  // fallbacks (hero, info column, badges) instead of bare skeletons while the
  // SCAPI product streams in. Distinct from the quick-nav `state` above.
  const tileSeed = useProductTileSeed();
  const [crossSellTotal, setCrossSellTotal] = useState(0);
  // `undefined` until the cross-sell section actually mounts and reports (it
  // only renders when it has items). So `crossSellItems != null` is the signal
  // that a cross-sell section is present — which is the only time the combined
  // "Total" makes sense. Quick view never renders cross-sell, so it stays
  // `undefined` there and the Total never shows.
  const [crossSellItems, setCrossSellItems] = useState<number | undefined>(
    undefined,
  );
  const [crossSellSelections, setCrossSellSelections] = useState<
    CrossSellItem[]
  >([]);
  // Mobile sticky ATC bar visibility, driven by StickyATCSentinel: the bar
  // shows only when the inline ATC is scrolled out of view (restores JP's
  // 79faebf0 scroll-gated behavior — an intentional divergence from prod's
  // always-on bar; see git blame / feedback_deviation_authority).
  const [showStickyATC, setShowStickyATC] = useState(false);
  // The inline qty+ATC wrapper; StickyATCSentinel watches this element to
  // toggle the sticky bar. Passed explicitly (rather than inferred from DOM
  // position) so reordering the surrounding markup can't silently break it.
  const inlineAtcRef = useRef<HTMLDivElement>(null);
  const { site } = useSite();
  // LMN (Letter of Medical Necessity) modal content, authored in Builder
  // site-preferences and resolved once in the layout loader (SFCC-11677).
  const { lmnContent } = useLayoutData();
  // BM-pref-driven free-shipping badge threshold (root loader); null = hidden.
  const freeShippingThreshold = useRootLayoutData().freeShipping.badgeThreshold;
  const [searchParams] = useSearchParams();

  const product = state?.product ?? _product;

  function scrollToReviews() {
    document
      .getElementById(pwrHeadlineId)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  function getMasterId(p: HecProduct) {
    if (!isVariantProduct(p)) return p.id ?? productId;
    // Native master.masterId first — c_parentMasterProductID is missing on
    // some catalog variants and wrong on others (SFCC-11921).
    return p.master?.masterId ?? p.c_parentMasterProductID ?? p.id ?? productId;
  }

  // Single source of truth for "will the PowerReviews widget render" — the
  // header's skeleton gate and the widget block below must never drift, or
  // gated-out products get a dead skeleton until the attr fallback fires.
  const willRenderPrWidget = (p: HecProduct) =>
    Boolean(powerReviewsConfig) && !isSetProduct(p);

  // The SKU-area eligibility chip. Rendered here (rather than inline at each
  // consumer) because telehealth PDPs hoist it ABOVE the title, which means the
  // same node has to be available to both the mobile and desktop title
  // placements as well as the standard column's own body.
  const renderEligibilityPill = (
    p: Record<string, unknown>,
    placement?: "mobile" | "desktop",
  ) => {
    const label = bySiteId(site.id, ELIGIBILITY_LABEL_BY_SITE);
    if (!p.c_isEligible || !label) return null;
    return (
      <EligibilitySummaryPill
        label={label}
        data-testid={
          placement
            ? `pdp-eligibility-badge-${placement}`
            : "pdp-eligibility-badge"
        }
      />
    );
  };

  // Title + "Shop more {brand}" — rendered above the image on mobile (devhec
  // parity) and in the info column at md+. Shared so both placements stay in
  // sync; the non-LMN title is a SelectedVariant consumer (works anywhere
  // inside VariantSelectorRegion).
  //
  // `eligibilityPill` is telehealth-only (ET-216): on a treatment PDP the chip
  // sits ABOVE the product name, so it has to travel with the title through
  // both placements rather than living in the column body where the standard
  // and LMN columns keep it.
  const renderTitleBrand = (
    p: SerializeFrom<C_ProductInfo>,
    {
      isLMN,
      placement,
      eligibilityPill,
    }: {
      isLMN: boolean;
      placement: "mobile" | "desktop";
      eligibilityPill?: ReactNode;
    },
  ) => (
    <>
      {eligibilityPill}
      {isLMN ? (
        <h1
          className="font-grotesque text-[1.4375rem] font-medium leading-[1.15] tracking-tight text-emphasis md:text-[2.0125rem]"
          data-testid={`pdp-product-name-${placement}`}
        >
          {p.name}
        </h1>
      ) : (
        <VariantSelector.SelectedVariant>
          {({ selected }) => (
            <h1
              className="font-grotesque text-[1.2rem] font-medium leading-[1.15] tracking-tight text-emphasis sm:text-[1.4375rem] md:text-[2.0125rem]"
              data-testid={`pdp-product-name-${placement}`}
            >
              {resolveVariantTitle({
                selected,
                variationAttributes: p.variationAttributes,
                variationValues: p.variationValues as
                  | Record<string, string>
                  | undefined,
                masterName: p.name ?? "",
                isTelehealth: isTelehealthProduct(
                  p as { c_ownerClass?: string },
                ),
              })}
            </h1>
          )}
        </VariantSelector.SelectedVariant>
      )}
      {renderBrandRow(p)}
    </>
  );

  // Hybrid PDP: triggered by ?hybrid=true OR product not orderable.
  //
  // Resolved in three tiers, most-authoritative first:
  //   1. `detectedHybrid` — what `HybridDetector` reported from inside the
  //      product <Await>. The only tier available after a soft nav, where the
  //      product is still a promise. It is the SINGLE state writer: a second
  //      `useEffect` here re-asserting `isHybridParam` used to fight it and win
  //      (a CHILD's effect flushes before its parent's), pinning the value at
  //      `isHybridParam` forever.
  //      Stored WITH the product it was measured on, and discarded in render
  //      when they disagree. Without that it survives a PDP→PDP soft nav: the
  //      new product is a promise for the whole navigation, so tier 2 is null
  //      by construction and tier 1 would still hold the PREVIOUS product's
  //      answer until the new detector's effect fires. Leaving a non-orderable
  //      product for a resolvable one would then paint HybridPDPSkeleton for
  //      the entire streaming window — with `{!isHybrid && …}` suppressing the
  //      normal layout, costing the nav the optimistic tile-seed hero
  //      (SFCC-11255). Reordering the tiers does NOT fix this; only forgetting
  //      the stale measurement does.
  //   2. `eagerHybrid` — the same computation run inline when the product is
  //      ALREADY resolved. `deferDataRequest` awaits it on document requests, so
  //      this covers the server render, where no effect ever runs. Without it,
  //      SSR reflected only the URL param: `?hybrid=true` on a telehealth
  //      product served a page with no product content at all (the normal layout
  //      suppressed, HybridPDP correctly declining), and a NON-ORDERABLE
  //      standard product served BOTH layouts — the normal PDP with a live Add
  //      to Cart alongside HybridPDP — with hydration then yanking one away.
  //   3. `isHybridParam` — last resort, only reachable on a soft nav before the
  //      product resolves, which is also when nothing is rendered yet.
  //
  // Keep this derived rather than stored: seeding `useState` from the param is
  // what produced a server frame that disagreed with the client.
  //
  // The rule itself lives in `isHybridPdp` (utils/hybrid-pdp.ts), shared with
  // the loader so it only builds the hybrid grids when this layout renders
  // (COM-830).
  const isHybridParam = hasHybridParam(searchParams);
  const eagerProduct =
    product instanceof Promise ? null : (product as HybridPdpProduct);
  const eagerHybrid = eagerProduct
    ? isHybridPdp(eagerProduct, searchParams)
    : null;
  const [detectedHybrid, setDetectedHybrid] = useHybridDetection(productId);
  const isHybrid = detectedHybrid ?? eagerHybrid ?? isHybridParam;

  return (
    <div className="">
      <div className="mx-auto pb-4 pt-4 sm:px-4 md:max-w-[1335px] lg:px-12">
        {/* Top feature-content module (`c_moduleTopFeatureContent`) — renders at
            the very top of the PDP, above the breadcrumbs, mirroring SFRA's
            `topContentModule` include at the head of `productDetails.isml`
            (SFCC-11805). Previously this slot was bundled with `1/2/3` in the
            grouped section above the accordions, so it rendered below the product
            details instead of at the top. Same `!pdpInlineSection` suppression as
            the grouped block (Builder inline content supersedes the whole BM
            feature-content block, SFCC-11656) and same WDH omission (the SFRA
            `app_wdh` cartridge strips this include). Keyed by master id so a
            PDP→PDP soft-nav remounts and re-runs script revival (SFCC-11655). */}
        {!pdpInlineSection && site.id !== "WDH" && (
          <Suspense fallback={null}>
            <Await resolve={product}>
              {product => (
                <FeatureContentModules
                  key={getMasterId(product as HecProduct)}
                  product={product as Record<string, unknown>}
                  includedKeys={["top"]}
                  className="mb-3 w-full space-y-6"
                  testId="feature-content-modules-top"
                />
              )}
            </Await>
          </Suspense>
        )}
        <div className="mx-auto flex w-full flex-row justify-between px-4 sm:px-0 md:max-w-[1335px]">
          <div className="w-full pb-2">
            {/* SkeletonAwait (not plain <Await>) so a resolved product/category
                renders the hook-using <Breadcrumbs> DIRECTLY in the SSR shell
                (no ResolveAwait subtree → no streaming suspend); only a deferred
                value on client nav goes through Suspense+Await. */}
            <SkeletonAwait
              resolve={product}
              skeleton={<BreadcrumbsSkeleton size={4} />}
            >
              {product => (
                <SkeletonAwait
                  resolve={productCategories}
                  skeleton={<BreadcrumbsSkeleton size={4} />}
                >
                  {productCategories => (
                    <Breadcrumbs
                      categories={productCategories}
                      pageName={product.name}
                      showHome={false}
                      mobileBackLink
                    />
                  )}
                </SkeletonAwait>
              )}
            </SkeletonAwait>
          </div>
          <div className="hidden sm:block">
            {state?.quickNav && (
              <ProductQuickNav
                product={product as SerializeFrom<C_ProductInfo>}
                state={state}
              />
            )}
          </div>
        </div>
        {/* Hybrid PDP: compact product + category grids.
            Fallback is gated on the resolved `isHybrid` because the hybrid
            Suspense resolves to nothing for non-hybrid products (the typical
            case) — unconditionally rendering a hybrid skeleton would flash +
            disappear on every PDP load. It shows only when a tier we can
            evaluate BEFORE the product resolves says hybrid: `?hybrid=true` in
            the URL, or a detection measured on THIS product id. A stale
            detection from the previously-viewed product deliberately doesn't
            qualify — see the tier list above. */}
        <Suspense fallback={isHybrid ? <HybridPDPSkeleton /> : null}>
          <Await resolve={product}>
            {product => {
              // ET-216: a telehealth product NEVER takes the hybrid path (the
              // hybrid layout renders its own ProductBuyForm + carousel) —
              // enforced inside `isHybridPdp`, shared with the loader.
              const computedHybrid = isHybridPdp(
                product as HybridPdpProduct,
                searchParams,
              );

              return (
                <>
                  <HybridDetector
                    productId={productId}
                    isHybrid={computedHybrid}
                    onHybridChange={setDetectedHybrid}
                  />
                  {computedHybrid && (
                    <HybridPDP
                      product={product as SerializeFrom<HecProduct>}
                      master={master}
                      productId={productId}
                      inStockWarningLevel={inStockWarningLevel}
                      hybridGrids={hybridGrids}
                    />
                  )}
                </>
              );
            }}
          </Await>
        </Suspense>

        {/* Normal PDP layout — hidden when hybrid */}
        {!isHybrid && (
          <VariantSelectorRegion
            product={
              product as
                | SerializeFrom<C_ProductInfo>
                | Promise<SerializeFrom<C_ProductInfo>>
            }
            master={master}
            productId={productId}
            variantDataMap={variantDataMap}
            fallback={
              // This Suspense fires on client-side nav while the deferred
              // `product` resolves inside VariantSelectorRegion (SFCC-11255).
              // Mirror the per-section optimistic-tile-seed fallbacks below
              // (pdp-optimistic-tile-nav) so the region-level suspension shows
              // the same optimistic hero/info from the clicked tile, not a bare
              // skeleton. Falls back to skeletons when there's no seed.
              <div className="min-[768px]:flex-row relative mx-auto flex w-full flex-col gap-[30px] md:max-w-[1335px]">
                {/* Mobile: title + brand sit above the image (devhec parity). */}
                <div className="min-[768px]:hidden flex w-full flex-col gap-3 px-5 sm:px-0">
                  {tileSeed ? (
                    <OptimisticTitleBrand seed={tileSeed} />
                  ) : (
                    <MobileTitleBrandSkeleton />
                  )}
                </div>
                <div className="min-[768px]:basis-[470px] min-[768px]:shrink min-[768px]:grow-0 min-[768px]:px-0 flex w-full flex-col items-center overflow-hidden px-3 sm:px-0">
                  {tileSeed ? (
                    <OptimisticHero seed={tileSeed} />
                  ) : (
                    <ProductImagesSkeleton />
                  )}
                </div>
                <div className="min-[768px]:min-w-[480px] min-[768px]:flex-1 flex w-full flex-col items-center">
                  {renderInfoColumnFallback(tileSeed, site.id)}
                </div>
              </div>
            }
          >
            <div
              className={cn(
                "min-[768px]:flex-row relative mx-auto flex w-full flex-col gap-[30px] md:max-w-[1335px]",
                state?.direction === "forward" &&
                  "animate-in slide-in-from-right",
                state?.direction === "back" && "animate-in slide-in-from-left",
              )}
              key={`reveal-${productId}`}
            >
              {/* Mobile: title + "Shop more {brand}" hoisted above the image to
                  match devhec/SFRA (`md:hidden`; the info column renders them at
                  md+). Inside VariantSelectorRegion so the non-LMN title stays a
                  SelectedVariant consumer. */}
              <div className="min-[768px]:hidden flex w-full flex-col gap-3 px-5 sm:px-0">
                <SkeletonAwait
                  resolve={product}
                  skeleton={
                    tileSeed ? (
                      <OptimisticTitleBrand seed={tileSeed} />
                    ) : (
                      <MobileTitleBrandSkeleton />
                    )
                  }
                >
                  {product => {
                    const p = product as Record<string, unknown>;
                    const isLMN = isLMNProduct(p as { c_ownerClass?: string });
                    return renderTitleBrand(
                      product as SerializeFrom<C_ProductInfo>,
                      {
                        isLMN,
                        placement: "mobile",
                        eligibilityPill: isTelehealthProduct(
                          p as { c_ownerClass?: string },
                        )
                          ? renderEligibilityPill(p, "mobile")
                          : undefined,
                      },
                    );
                  }}
                </SkeletonAwait>
              </div>

              <div
                className={
                  "min-[768px]:basis-[470px] min-[768px]:shrink min-[768px]:grow-0 min-[768px]:px-0 flex w-full flex-col items-center overflow-hidden px-3 sm:px-0"
                }
              >
                <SkeletonAwait
                  resolve={product}
                  skeleton={
                    tileSeed ? (
                      <OptimisticHero seed={tileSeed} />
                    ) : (
                      <ProductImagesSkeleton />
                    )
                  }
                >
                  {product => (
                    <ProductImages
                      product={product as SerializeFrom<C_ProductInfo>}
                      // ET-213 hides the carousel on treatment PDPs (one image
                      // per treatment) along with the favourites heart and the
                      // zoom/lightbox. The Rx badge overlay stays.
                      staticImage={isTelehealthProduct(
                        product as { c_ownerClass?: string },
                      )}
                    />
                  )}
                </SkeletonAwait>
              </div>

              <div className="min-[768px]:min-w-[480px] min-[768px]:flex-1 flex w-full flex-col items-center">
                {/* Info + variant selector + cross-sell + ATC — info wraps inside
                    VariantSelector so title / SKU / price react to variant clicks. */}
                <SkeletonAwait
                  resolve={product}
                  skeleton={renderInfoColumnFallback(tileSeed, site.id)}
                >
                  {product => {
                    const p = product as Record<string, unknown>;
                    const typedProduct =
                      product as SerializeFrom<C_ProductInfo>;
                    const isLMN = p.c_ownerClass === "LMN";
                    const rating = Number(p.c_averageRating ?? 0);
                    const reviewCount = Number(p.c_totalReviews ?? 0);
                    const externalLink = p.c_externalLink as string | undefined;

                    const ratingsRow = (
                      // Skeleton until the PR widget below reports the live
                      // rollup, then that; catalog attrs only as the failure
                      // fallback — they go stale when reviews drop out of PR
                      // (SFCC-11800).
                      <PowerReviewsSyncedRatings
                        pageId={getMasterId(product as HecProduct)}
                        rating={rating}
                        reviewCount={reviewCount}
                        enabled={willRenderPrWidget(product as HecProduct)}
                        onClick={scrollToReviews}
                      />
                    );

                    // COM-180: SFRA's `shortDescriptionTruncated`, sanitised
                    // and cut, with Read More only when the accordion block
                    // renders — the same `hasAccordionBlock` as that block's
                    // own null guard below.
                    const descriptionBlock = (
                      <PdpShortDescription
                        shortDescription={resolveShortDescription(p)}
                        hasReadMoreTarget={hasAccordionBlock(
                          p,
                          hasBuilderAccordions,
                        )}
                      />
                    );

                    // ET-216 — telehealth (GLP-1) treatment column. Placed
                    // before the LMN branch and shaped the same way: an early
                    // return that replaces the whole transactional block (qty +
                    // ATC, cross-sell, variant options, promo callouts,
                    // compare-to, cross-promo) rather than gating each piece.
                    if (isTelehealthProduct(p as { c_ownerClass?: string })) {
                      // Fail-closed: no resolvable condition ⇒ no CTA. An Rx
                      // product must not fall back to Add to Cart.
                      const treatmentSlug = treatmentByConditionType(
                        p.c_th_condition_type as string | undefined,
                      )?.slug;
                      return (
                        <TelehealthInfoColumn
                          product={typedProduct}
                          siteLabel={bySiteId(
                            site.id,
                            ELIGIBILITY_SITE_LABEL_BY_SITE,
                          )}
                          treatmentSlug={treatmentSlug}
                          // Only link when a How-it-Works section is actually
                          // authored. Neither `belowFoldSections` nor
                          // `glp1Regions.belowFold` is the right gate: the
                          // former also counts the `pdp-inline-content` push,
                          // the latter this entry's demoted canvas (usually the
                          // shared FAQ Symbol). Both produce a link labelled
                          // for content it doesn't lead to. The anchor wraps
                          // the whole below-fold stack, so the target exists
                          // either way — this gate is about the label being
                          // true.
                          howItWorksHref={
                            glp1HasHowItWorks
                              ? `#${HOW_IT_WORKS_ANCHOR_ID}`
                              : undefined
                          }
                          titleBrand={renderTitleBrand(typedProduct, {
                            isLMN: false,
                            placement: "desktop",
                            eligibilityPill: renderEligibilityPill(
                              p,
                              "desktop",
                            ),
                          })}
                          ratings={ratingsRow}
                          price={
                            <VariantSelector.SelectedVariant>
                              {({
                                currentPrice,
                                salePrice,
                                selectedPriceInfo,
                              }) => (
                                <TreatmentPrice
                                  display={resolvePriceFromSelectedVariant(
                                    { currentPrice, salePrice },
                                    p,
                                    selectedPriceInfo,
                                  )}
                                  period={telehealthPricePeriod(typedProduct)}
                                />
                              )}
                            </VariantSelector.SelectedVariant>
                          }
                          pricingSectionProvides={glp1BuyColumnProvides}
                          pricingSection={
                            glp1BuyColumnSection ? (
                              // The LIVE price is published through context so
                              // the block's "use default price" toggle resolves
                              // through the same path the coded column uses,
                              // rather than a second derivation that can drift.
                              <VariantSelector.SelectedVariant>
                                {({
                                  currentPrice,
                                  salePrice,
                                  selectedPriceInfo,
                                }) => (
                                  <TelehealthPdpPriceProvider
                                    value={{
                                      display: resolvePriceFromSelectedVariant(
                                        { currentPrice, salePrice },
                                        p,
                                        selectedPriceInfo,
                                      ),
                                      period:
                                        telehealthPricePeriod(typedProduct),
                                      ctaContext: PDP_CTA_CONTEXT,
                                    }}
                                  >
                                    <Suspense fallback={null}>
                                      <PrefetchedBuilderContent
                                        model={ModelMap.pdpGlp1Pricing}
                                        content={glp1BuyColumnSection}
                                      />
                                    </Suspense>
                                  </TelehealthPdpPriceProvider>
                                )}
                              </VariantSelector.SelectedVariant>
                            ) : undefined
                          }
                          analytics={
                            <VariantSelector.SelectedVariant>
                              {({ variant, salePrice, currentPrice }) => (
                                <>
                                  <SegmentViewItemTracker
                                    product={typedProduct}
                                    categories={productCategories}
                                    siteId={site.id}
                                    variant={variant}
                                    price={salePrice ?? currentPrice}
                                  />
                                  <CioItemDetailTracker
                                    productId={typedProduct.id}
                                    productName={typedProduct.name}
                                    variant={variant}
                                  />
                                </>
                              )}
                            </VariantSelector.SelectedVariant>
                          }
                        />
                      );
                    }

                    if (isLMN) {
                      return (
                        <>
                          <div className="relative flex w-full flex-col gap-3 px-5 sm:px-0">
                            <div className="space-y-3">
                              {/* Title + brand: hoisted above the image on
                                    mobile (see the reveal div); in-column at md+. */}
                              <div className="min-[768px]:flex min-[768px]:flex-col min-[768px]:gap-3 hidden">
                                {renderTitleBrand(
                                  product as SerializeFrom<C_ProductInfo>,
                                  { isLMN: true, placement: "desktop" },
                                )}
                              </div>
                              {product.id && (
                                <p
                                  className="text-xs text-gray-500"
                                  data-testid="pdp-sku"
                                >
                                  SKU# {product.id}
                                </p>
                              )}
                              {ratingsRow}
                              {descriptionBlock}
                            </div>

                            {/* LMN product: no price, no ATC, external link */}
                            <div className="mt-4 space-y-4">
                              <WhatsLmnButton
                                title={lmnContent.modalTitle}
                                content={lmnContent.modalContent}
                                link={lmnContent.modalLink}
                              />
                              {externalLink && (
                                <a
                                  href={externalLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="block w-full rounded bg-primary px-6 py-3 text-center font-medium text-primary-foreground"
                                >
                                  Shop Now
                                  <svg
                                    className="ml-2 inline h-4 w-4"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                  >
                                    <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3" />
                                  </svg>
                                </a>
                              )}
                              <p className="text-xs text-gray-500">
                                Excluded from promotions and coupons
                              </p>
                            </div>
                          </div>

                          <CrossSellProducts
                            product={p}
                            crossSell={crossSell}
                            onTotalsChange={(total, items) => {
                              setCrossSellTotal(total);
                              setCrossSellItems(items);
                            }}
                            onSelectionsChange={setCrossSellSelections}
                          />
                        </>
                      );
                    }

                    return (
                      <>
                        {/* Info block — title / SKU / price are SelectedVariant consumers. */}
                        <div className="relative flex w-full flex-col gap-3 px-5 sm:px-0">
                          <div className="space-y-3">
                            {/* Title + brand: hoisted above the image on
                                  mobile (see the reveal div); in-column at md+. */}
                            <div className="min-[768px]:flex min-[768px]:flex-col min-[768px]:gap-3 hidden">
                              {renderTitleBrand(typedProduct, {
                                isLMN: false,
                                placement: "desktop",
                              })}
                            </div>

                            <VariantSelector.SelectedVariant>
                              {({ variant }) => (
                                <p
                                  className="text-xs text-gray-500"
                                  data-testid="pdp-sku"
                                >
                                  SKU# {variant?.productId ?? product.id}
                                </p>
                              )}
                            </VariantSelector.SelectedVariant>

                            {/* Eligibility summary pill — mirrors SFRA
                                  `eligibility.isml` summary mode: bordered chip
                                  above the rating. SFRA gate:
                                  `product.isEligible && showEligibility (site pref)
                                  && !product.isBonusProductLineItem`. Workers map:
                                  c_isEligible; showEligibility → per-site label
                                  non-null (all three sites; WDH = "FSA/HSA
                                  Eligible", see pdp-shared-constants.ts);
                                  isBonusProductLineItem
                                  is a basket-only flag, structurally N/A on the PDP.
                                  Colors: per-site --eligibility-pill-border +
                                  --eligibility-pill-text (app.css, prod parity);
                                  the check icon (fill=currentColor) inherits
                                  --eligibility-pill-text from the wrapper. */}
                            {renderEligibilityPill(p)}

                            {ratingsRow}
                            {descriptionBlock}
                          </div>

                          <PromotionCallouts
                            promotions={(p as HecProduct).productPromotions}
                          />
                          <div className="pt-6">
                            <VariantSelector.SelectedVariant>
                              {({
                                currentPrice,
                                salePrice,
                                selectedPriceInfo,
                              }) => (
                                <PriceLabel
                                  display={resolvePriceFromSelectedVariant(
                                    { currentPrice, salePrice },
                                    p,
                                    selectedPriceInfo,
                                  )}
                                  className="font-grotesque text-[1.75rem] font-medium leading-none text-product-price"
                                  salePriceClassName="text-sale-price font-bold"
                                  data-testid="pdp-price"
                                />
                              )}
                            </VariantSelector.SelectedVariant>
                            {freeShippingThreshold != null &&
                              Number(p.price ?? 0) >= freeShippingThreshold && (
                                <p className="mt-5 font-grotesque text-sm font-normal text-accent">
                                  This item ships free!
                                </p>
                              )}
                          </div>
                        </div>

                        {/* SFCC-11012 (ANALYTICS-16/-17): Segment View Item —
                              fires on PDP load + variant change. Renders null;
                              reads the resolved variant/price from the
                              SelectedVariant render prop and resolves the
                              (deferred) category breadcrumb inside its own
                              effect — no render-side Suspense/Await needed. */}
                        <VariantSelector.SelectedVariant>
                          {({ variant, salePrice, currentPrice }) => (
                            <>
                              <SegmentViewItemTracker
                                product={typedProduct}
                                categories={productCategories}
                                siteId={site.id}
                                variant={variant}
                                price={salePrice ?? currentPrice}
                              />
                              {/* SFCC-11906: CIO item_detail_load beacon —
                                  once per viewed product, variationId from the
                                  selected variant. */}
                              <CioItemDetailTracker
                                productId={typedProduct.id}
                                productName={typedProduct.name}
                                variant={variant}
                              />
                            </>
                          )}
                        </VariantSelector.SelectedVariant>

                        <CompareToMessage
                          data={p.compareToData as CompareToData | undefined}
                          siteId={site.id}
                        />

                        {/* Cross-promo messaging (SFCC-11468 / PDP-WDH-01) —
                            merchant-authored HTML, date-gated via DisplayFrom/To.
                            Mirrors SFRA position: after price + compareToMsg
                            (org_hec + app_wdh render identically; no per-site
                            gating). Renders raw HTML to match the trust model
                            used by sibling ProductMessaging (PLP tile) and
                            BasketLineItemCrossPromo (cart). Per-site color
                            mirrors SFRA's $pdp-cross-message SCSS var:
                            FSA=$blue4, HSA=$indigo4, WDH=$purple4. Column
                            gutter padding mirrors CompareToMessage's
                            non-compact pattern (SFCC-11335 precedent). */}
                        {typedProduct.c_crossPromoMessaging &&
                          isScheduled(
                            typedProduct.c_crossPromoMessagingDisplayFrom,
                            typedProduct.c_crossPromoMessagingDisplayTo,
                          ) && (
                            <div
                              className={cn(
                                "mt-2 w-full px-5 text-sm sm:px-0",
                                bySiteId(site.id, {
                                  FSASTORE: "text-[#142745]",
                                  HSASTORE: "text-[#55449A]",
                                  WDH: "text-[#431340]",
                                }),
                              )}
                              data-testid="pdp-cross-promo"
                              dangerouslySetInnerHTML={{
                                __html: rewriteSfccContentLinks(
                                  typedProduct.c_crossPromoMessaging,
                                ),
                              }}
                            />
                          )}

                        {/* Variant options — directly under the price */}
                        <div className="w-full px-5 sm:px-0">
                          <ProductVariantOptions
                            product={typedProduct}
                            master={master}
                            wrapOptions
                          />
                        </div>

                        {/* Cross-sell: "Complete your purchase with:" */}
                        <CrossSellProducts
                          product={p}
                          crossSell={crossSell}
                          onTotalsChange={(total, items) => {
                            setCrossSellTotal(total);
                            setCrossSellItems(items);
                          }}
                          onSelectionsChange={setCrossSellSelections}
                        />

                        {/* Qty + ATC — below cross-sell. The inline ("main")
                              ATC now shows on mobile too (not just >=992px) so
                              JP's sentinel has a real button to scroll past. */}
                        <div ref={inlineAtcRef} className="w-full px-5 sm:px-0">
                          <ProductBuyForm
                            product={typedProduct}
                            productId={productId}
                            master={master}
                            wrapOptions
                            externalVariantContext
                            inStockWarningLevel={inStockWarningLevel}
                            crossSellTotal={crossSellTotal}
                            crossSellItems={crossSellItems}
                            crossSellSelections={crossSellSelections}
                            showLoyalty={!!site.loyalty}
                            acLoyaltyPoints={acLoyaltyPoints}
                          />
                        </div>

                        {/* Reveal the mobile sticky bar whenever the inline
                              ATC above is scrolled out of view (in either
                              direction); hide it while any part is visible. */}
                        <StickyATCSentinel
                          targetRef={inlineAtcRef}
                          onVisibilityChange={setShowStickyATC}
                        />
                      </>
                    );
                  }}
                </SkeletonAwait>

                {/* Eligibility + Free Shipping badges below ATC (restored from
                    96f27e88's removal — the row does exist on prod alongside
                    the SKU-area eligibility pill).
                    ET-216: dropped on telehealth PDPs. It pairs with the ATC
                    the treatment column doesn't have, and its "100% … ELIGIBLE"
                    duplicates the chip now sitting above the product name.
                    NOTE this diverges from ET-213, which listed both badges as
                    elements to display. */}
                <SkeletonAwait
                  resolve={product}
                  skeleton={
                    tileSeed ? (
                      <OptimisticBadges seed={tileSeed} siteId={site.id} />
                    ) : (
                      <BadgesSkeleton />
                    )
                  }
                >
                  {product => {
                    const p = product as Record<string, unknown>;
                    if (isTelehealthProduct(p as { c_ownerClass?: string })) {
                      return null;
                    }
                    const isEligible = !!p.c_isEligible;
                    const showsFreeShipping = bySiteId(
                      site.id,
                      SHOWS_FREE_SHIPPING_BADGE,
                    );
                    // Short label (just the site identifier) for the
                    // eligibility chip — derived from the full label by
                    // dropping " Eligible".
                    const siteLabel =
                      bySiteId(site.id, ELIGIBILITY_SITE_LABEL_BY_SITE) ?? "";

                    return (
                      <section
                        aria-label="product-badges"
                        className="min-[768px]:pl-5 min-[768px]:pr-0 mt-3 flex w-full flex-wrap items-center gap-4 border-t border-[#ccc] pb-0 pl-5 pt-4 sm:px-0"
                      >
                        {/* The "100% … ELIGIBLE" badge is the Workers pairing
                            with the free-shipping badge — FSA/HSA only. WDH has
                            no free shipping, so its eligibility shows via the
                            SKU-area summary pill alone (SFCC-11640). */}
                        {isEligible && siteLabel && showsFreeShipping && (
                          <EligibilityBadge siteLabel={siteLabel} />
                        )}
                        {showsFreeShipping && <FreeShippingBadge />}
                      </section>
                    );
                  }}
                </SkeletonAwait>

                {/* Bundle contained items (SFCC-11826) — SFRA bundleDetails
                    renders bundleItems below the buy block, before the
                    description accordions. Renders null for non-bundles. */}
                <Suspense fallback={null}>
                  <Await resolve={product}>
                    {product => (
                      <BundleItems
                        product={product as HecProduct}
                        variant="pdp"
                      />
                    )}
                  </Await>
                </Suspense>
              </div>
            </div>

            {/* PDP Global Content — the every-PDP sibling of Inline Content,
                rendered directly above it. The loader keeps the untargeted
                Builder fallback for this model (that entry IS the site-wide
                default); targeted entries override it per product. Additive
                only: unlike a targeted Inline entry (SFCC-11656), Global does
                NOT suppress the BM FeatureContentModules block.

                Deliberately OUTSIDE the anchor wrapper below: the site-wide
                default is not what the telehealth column's "How it Works" link
                promises, so the jump target starts after it. */}
            {pdpGlobalSection && (
              <div className="pt-5" data-testid="pdp-global-content">
                {/* Section-level Suspense so a suspending Builder custom
                    component is contained here and can't bubble past the route
                    <Outlet> into a page-level skeleton. */}
                <Suspense fallback={null}>
                  <PrefetchedBuilderContent
                    model={ModelMap.pdpGlobalContent}
                    content={pdpGlobalSection}
                  />
                </Suspense>
              </div>
            )}

            {belowFoldSections.length > 0 && (
              // ET-216: `id` is the jump target for the telehealth column's
              // "How it Works" link, and `scroll-mt-24` keeps the heading clear
              // of the sticky header. Harmless on standard PDPs (nothing links
              // to it). Kept on the WRAPPER rather than inside the Builder
              // content so an editor rearranging blocks can't break the link —
              // and on the wrapper rather than the first section so it stays
              // put when only one of the two models has an entry.
              <div className="scroll-mt-24 pt-5" id={HOW_IT_WORKS_ANCHOR_ID}>
                {/* Section-level Suspense so a suspending Builder custom
                    component is contained here and can't bubble past the route
                    <Outlet> into a page-level skeleton. Per section, so one
                    model suspending doesn't blank the other. */}
                {belowFoldSections.map(section => (
                  <Suspense key={section.key} fallback={null}>
                    <PrefetchedBuilderContent
                      model={section.model}
                      content={section.content}
                    />
                  </Suspense>
                ))}
              </div>
            )}

            {/* Feature content modules `1/3` (`c_module{1,3}FeatureContent`) —
                grouped with the Builder inline content directly above the
                accordions (SFCC-11615). The `top` slot is NOT here: it renders
                separately at the page top above the breadcrumbs (SFCC-11805).
                Module `2` is NOT here either: it is the below-accordions
                fallback (COM-793, SFRA rendered it in the accordion template),
                so this block is `["1","3"]` on every site.
                Audit row: PDP-WDH-01.

                SFCC-11656: when a specifically-targeted Builder PDP Inline Content
                section is set up for this PDP (`pdpInlineSection` truthy — the
                loader has already dropped the untargeted global fallback, so this
                is only ever a productId/categoryId/brand/name-targeted entry), it
                supersedes the BM feature-content block entirely, so the whole
                `FeatureContentModules` is suppressed (both here and the page-top
                `top` slot). This avoids rendering the same migrated content (e.g.
                Fastr/Zmags embeds) twice regardless of which slot holds it — the
                Builder entry is the single source of truth. That includes an
                entry whose content is only in its `belowAccordions` region
                (COM-793) — intended, not a bug. Module 2 follows the same rule
                from its own slot below the accordions (see
                `InlineBelowAccordionsSlot`): Builder's `belowAccordions`
                region wins, else module 2 only when no targeted entry exists. */}
            {!pdpInlineSection && (
              <Suspense fallback={<FeatureContentModulesSkeleton />}>
                <Await resolve={product}>
                  {product => (
                    // Key by master id so a PDP→PDP soft-nav remounts the section:
                    // the modules re-run their script revival for the new product
                    // instead of reusing a stale instance (same SDK-reuse class as
                    // the PowerReviews per-master keys below). Master-level so a
                    // variant swap doesn't churn it. (SFCC-11655.)
                    <FeatureContentModules
                      key={getMasterId(product as HecProduct)}
                      product={product as Record<string, unknown>}
                      includedKeys={["1", "3"]}
                    />
                  )}
                </Await>
              </Suspense>
            )}

            {/* `c_module2Text` brand banner — full-bleed, dark primary bg, centered serif copy.
                Ports SFRA's `.c-product-detail__info__modules__module--module2` block. Used
                operationally for the Caring Mill brand callout but not gated on brand — any
                product with `c_module2Text` populated will render here. Positioned below the
                Builder inline content and directly above the product accordions (Description
                first) to match production placement (SFCC-10584). */}
            <Suspense fallback={null}>
              <Await resolve={product}>
                {product => {
                  const p = product as Record<string, unknown>;
                  const html = p.c_module2Text as string | undefined;
                  if (!html) return null;
                  const textColorClass = "text-pdp-banner-text";
                  return (
                    <section
                      data-testid="pdp-module2-banner"
                      className="relative left-1/2 right-1/2 my-12 -ml-[50vw] -mr-[50vw] flex min-h-[195px] w-screen items-center justify-center overflow-x-clip bg-primary"
                    >
                      <div
                        className={`mx-auto max-w-[1200px] p-[30px] text-center font-serif text-size-225 leading-normal md:text-size-heading-lg ${textColorClass}`}
                        dangerouslySetInnerHTML={{
                          __html: sanitizeHtml(rewriteSfccContentLinks(html)),
                        }}
                      />
                    </section>
                  );
                }}
              </Await>
            </Suspense>

            {/* Product info accordions — full width below both columns, matching production */}
            <Suspense fallback={<PDPAccordionsSkeleton />}>
              <Await resolve={product}>
                {product => {
                  const p = product as Record<string, unknown>;
                  // Treatment PDPs hide HIDDEN_ACCORDIONS_ON_TELEHEALTH by key;
                  // the rest stay — Shipping Restrictions in particular can be
                  // legally load-bearing on a prescription product, so this
                  // filters by key rather than emptying the list. Delivery &
                  // Returns and Loyalty (Builder-sourced, below) also still
                  // apply, and the `hasAccordionBlock` guard drops the whole
                  // block when nothing is populated (the same check gates the
                  // short description's Read More link).
                  // Memoised per product: the Read More gate above already
                  // computed these for the same object.
                  const sections = accordionSectionsFor(p);
                  if (!hasAccordionBlock(p, hasBuilderAccordions)) return null;

                  return (
                    <section
                      id="collapsible-description"
                      aria-labelledby="details-heading"
                      className="mt-8 w-full px-5 sm:px-0"
                    >
                      <PDPAccordion
                        type="multiple"
                        defaultValue={["description", "details"]}
                      >
                        {sections.map(({ key, label, content }) => (
                          <PDPAccordionItem key={key} value={key}>
                            <PDPAccordionTrigger
                              data-testid={`pdp-accordion-trigger-${key}`}
                            >
                              {label}
                            </PDPAccordionTrigger>
                            <PDPAccordionContent
                              data-testid={`pdp-accordion-content-${key}`}
                            >
                              <div
                                className="prose prose-sm max-w-none text-inherit"
                                dangerouslySetInnerHTML={{ __html: content }}
                              />
                            </PDPAccordionContent>
                          </PDPAccordionItem>
                        ))}
                        {pdpDeliveryReturnsSection && (
                          <PDPAccordionItem value="deliveryReturns">
                            <PDPAccordionTrigger data-testid="pdp-accordion-trigger-delivery-returns">
                              Delivery &amp; Returns
                            </PDPAccordionTrigger>
                            <PDPAccordionContent data-testid="pdp-accordion-content-delivery-returns">
                              <PrefetchedBuilderContent
                                model={ModelMap.pdpDeliveryReturns}
                                content={pdpDeliveryReturnsSection}
                              />
                            </PDPAccordionContent>
                          </PDPAccordionItem>
                        )}
                        {pdpLoyaltySection && (
                          <PDPAccordionItem value="loyalty">
                            <PDPAccordionTrigger data-testid="pdp-accordion-trigger-loyalty">
                              Loyalty
                            </PDPAccordionTrigger>
                            <PDPAccordionContent data-testid="pdp-accordion-content-loyalty">
                              <PrefetchedBuilderContent
                                model={ModelMap.pdpLoyalty}
                                content={pdpLoyaltySection}
                              />
                            </PDPAccordionContent>
                          </PDPAccordionItem>
                        )}
                      </PDPAccordion>
                    </section>
                  );
                }}
              </Await>
            </Suspense>

            {/* COM-793: the second PDP Inline Content position — the entry's
                `belowAccordions` region, below the product accordions (SFRA's
                "Featured Content Module 2" placement). Builder region first;
                else nothing when a targeted inline entry exists (SFCC-11656,
                module 2 is part of the superseded BM block); else BM
                `c_module2FeatureContent`. Rules + rationale live on the slot. */}
            <InlineBelowAccordionsSlot
              region={inlineBelowAccordions}
              hasInlineSection={Boolean(pdpInlineSection)}
              product={product}
              getMasterId={p => getMasterId(p as HecProduct)}
            />

            {/* "Frequently Bought Together" (pod `item_page_1`) — SFRA's
                `pdp-recommendations-1`, which renders after the product
                details/accordions and above Reviews. Inside the page's
                max-w-[1335px] container, so no extra width wrapper needed. */}
            <GatedPdpRecsCarousel
              recs={itemPageRecs}
              product={product}
              className="mt-10 w-full"
            />

            {/* Heading + containers SSR; widget client-renders into them after
                the SDK loads. All IDs/subject fields key off master per SFRA. */}
            {/* PowerReviewsWidget injects the `pwr` script + initializes
                    the containers rendered by <PowerReviewsDisplay /> +
                    <PowerReviewsQA />. It renders no visible UI of its own,
                    so there's nothing to skeleton — fallback stays null. */}
            {powerReviewsConfig && (
              <>
                <PowerReviewsHeading>Reviews</PowerReviewsHeading>

                <Suspense fallback={null}>
                  <Await resolve={product}>
                    {product => {
                      const p = product as HecProduct;
                      if (!willRenderPrWidget(p)) return null;
                      const masterId = getMasterId(p);
                      const largeImage = getVariantImage(
                        p as unknown as C_ProductInfo,
                        undefined,
                        "large",
                      )?.[0]?.link;
                      return (
                        <>
                          {/* Keys force a remount per product: on SPA PDP→PDP
                              nav React would otherwise reuse the container
                              nodes (mutating only the id attribute), leaving
                              the SDK-injected markup of the previous product
                              in place — the next pwr("render") then appends a
                              duplicate widget beside it (SFCC-11414). */}
                          <PowerReviewsSnippet
                            key={`pr-snippet-${masterId}`}
                            productId={masterId}
                          />
                          <PowerReviewsDisplay
                            key={`pr-display-${masterId}`}
                            productId={masterId}
                          />
                          {/* Q&A container ids are product-independent
                              (pr-questionsnippet/-display), so this key is the
                              ONLY thing forcing fresh Q&A nodes per product —
                              don't drop it as redundant. */}
                          <PowerReviewsQA key={`pr-qa-${masterId}`} />
                          <PowerReviewsWidget
                            config={powerReviewsConfig}
                            productId={masterId}
                            productName={p.name ?? ""}
                            productUrl={buildProductUrl(masterId, p.name)}
                            productImageUrl={largeImage ?? p.image?.link}
                            productDescription={p.c_siteShortDescription}
                            productUpc={p.upc}
                            productBrand={p.brand}
                            productPrice={p.price}
                            productInStock={p.inventory?.orderable === true}
                          />
                        </>
                      );
                    }}
                  </Await>
                </Suspense>
              </>
            )}

            {/* PDP Articles — Builder-sourced, WDH only. Removed on FSA + HSA
                (SFCC-11623). Gated so disabled sites render no skeleton or
                empty container, and the loader skips the upstream fetch. */}
            {showPdpArticles && (
              <SkeletonAwait
                resolve={pdpArticles}
                skeleton={<PDPArticlesSkeleton />}
              >
                {data => {
                  const { heading, articles } = data as {
                    heading: string | null;
                    articles: Array<{
                      id: string;
                      name: string;
                      imageUrl?: string;
                      description?: string;
                      url: string;
                    }>;
                  };
                  if (!articles?.length) return null;
                  return (
                    <PrefetchBuilderAnchors>
                      <PDPArticles
                        heading={heading || undefined}
                        articles={articles}
                      />
                    </PrefetchBuilderAnchors>
                  );
                }}
              </SkeletonAwait>
            )}

            {/* Sticky ATC bar — mobile/tablet only (<992px), never desktop.
                Scroll-gated: shows only while the inline ATC is out of view
                (StickyATCSentinel -> showStickyATC; JP's 79faebf0 pattern).
                Rendered INSIDE VariantSelectorRegion so it shares the lifted
                VariantSelector provider and submits the resolved variant SKU,
                not the master productId (SFCC-11390). */}
            <Suspense fallback={null}>
              <Await resolve={product}>
                {product => {
                  const p = product as Record<string, unknown>;
                  // ET-216: no sticky ATC on a treatment PDP — there is no
                  // inline ATC for its sentinel to shadow, and the bar is the
                  // one Add-to-Cart the info column's early return can't reach.
                  if (isTelehealthProduct(p as { c_ownerClass?: string })) {
                    return null;
                  }
                  const isEligible = !!p.c_isEligible;
                  const siteLabel =
                    bySiteId(site.id, ELIGIBILITY_SITE_LABEL_BY_SITE) ?? "";
                  const showsFreeShipping =
                    bySiteId(site.id, SHOWS_FREE_SHIPPING_BADGE) ?? false;

                  return showStickyATC ? (
                    <div className="min-[992px]:hidden">
                      <StickyATCBar
                        productId={productId}
                        isOrderable={
                          p.inventory
                            ? (p.inventory as Record<string, unknown>)
                                ?.orderable !== false
                            : true
                        }
                        stock={
                          (p.inventory as { ats?: number } | undefined)?.ats
                        }
                        crossSellSelections={crossSellSelections}
                        isEligible={isEligible}
                        siteLabel={siteLabel}
                        showsFreeShipping={showsFreeShipping}
                      />
                    </div>
                  ) : null;
                }}
              </Await>
            </Suspense>
          </VariantSelectorRegion>
        )}
      </div>
      {!isHybrid && (
        <>
          {/* Tabs loader value is stubbed to empty today
              (see product-detail-loader.ts). A sized skeleton would
              reserve space and then collapse on every PDP, making
              CLS worse than `fallback={null}`. Switch back to
              `<PDPTabsSkeleton />` once the Builder fetch lands. */}
          <Suspense fallback={null}>
            <Await resolve={originalTabsContent}>
              {content => <PDPTabsRender content={content} />}
            </Await>
          </Suspense>

          {/* `relatedProducts` (pod `pdp-2`) — SFRA's `pdp-recommendations-2`,
              the lower of the two PDP recs carousels. The upper carousel
              (`itemPageRecs` / pod `item_page_1` = `pdp-recommendations-1`)
              renders inside VariantSelectorRegion just above Reviews. See
              product-detail-loader.ts for the pod-id → content-asset mapping. */}
          <GatedPdpRecsCarousel
            recs={relatedProducts}
            product={product}
            className="mx-auto mt-4 w-full px-4 sm:px-4 md:max-w-[1335px] md:px-10"
          />
        </>
      )}
    </div>
  );
}
